import React from 'react';
import type { GamificationRule, GamificationRuleKey } from '@troia-v3/types';

import { Input, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { SettingsSection } from './SettingsSection';

const RULE_COPY: Record<GamificationRuleKey, { name: string; sub: string }> = {
  lead_captured: { name: 'Lead captado e confirmado', sub: 'Conta só depois que o cliente manda a mensagem no WhatsApp. Sem canal no funil, conta na hora.' },
  form_completed: { name: 'Formulário de captura preenchido', sub: 'Formulário do funil respondido pelo vendedor e vinculado ao lead.' },
  lead_won: { name: 'Venda fechada', sub: 'Lead marcado como ganho com valor. Vai para o responsável no momento da venda.' },
};
const ORDER: GamificationRuleKey[] = ['lead_captured', 'form_completed', 'lead_won'];

interface Props { rules: GamificationRule[]; onChange: (rules: GamificationRule[]) => void; disabled?: boolean }

const toInt = (raw: string): number | undefined => (raw.trim() === '' ? undefined : Math.max(0, Math.floor(Number(raw.replace(/\D/g, '')) || 0)));

/**
 * Validação espelho do Zod (`ruleSchema` em `gamification/validation.ts`):
 * `points` inteiro 1–1000, `dailyCap` (quando presente) 1–10000, `reversalDays`
 * (quando presente) 1–365. Evita 422 sem explicação ao salvar.
 */
export function rulesError(rules: GamificationRule[]): string | null {
  for (const rule of rules) {
    const name = RULE_COPY[rule.key].name;
    if (!Number.isInteger(rule.points) || rule.points < 1 || rule.points > 1000) {
      return `"${name}": pontos precisam ser um número inteiro entre 1 e 1000`;
    }
    if (rule.dailyCap !== undefined && (!Number.isInteger(rule.dailyCap) || rule.dailyCap < 1 || rule.dailyCap > 10000)) {
      return `"${name}": limite por dia precisa ser um número inteiro entre 1 e 10.000`;
    }
    if (rule.reversalDays !== undefined && (!Number.isInteger(rule.reversalDays) || rule.reversalDays < 1 || rule.reversalDays > 365)) {
      return `"${name}": prazo de estorno precisa ser um número inteiro entre 1 e 365 dias (deixe vazio para não ter prazo)`;
    }
  }
  return null;
}

export const PointsRulesTable: React.FC<Props> = ({ rules, onChange, disabled = false }) => {
  const patch = (key: GamificationRuleKey, p: Partial<GamificationRule>) =>
    onChange(rules.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const ordered = ORDER.map((k) => rules.find((r) => r.key === k)).filter((r): r is GamificationRule => r !== undefined);
  const error = rulesError(rules);

  return (
    <SettingsSection icon="star" title="Tabela de pontos" description="O que cada evento vale. Fica visível para todo vendedor no dashboard — sem fórmula escondida. Mudar um valor só afeta pontos novos." testId="gamification-section-rules">
      <div className="overflow-x-auto">
        <table className="gm-tbl" data-testid="gamification-rules-table">
          <thead>
            <tr>
              <th>Evento</th>
              <th className="r">Pontos</th>
              <th className="r">Limite por dia</th>
              <th className="r">
                Estorno
                {/* hint do campo: vazio = sem prazo (estorna sempre) */}
                <div style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, fontSize: 10.5, marginTop: 2 }}>vazio = sem prazo</div>
              </th>
              <th className="r">Ativo</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((rule) => (
              <tr key={rule.key} data-testid={`gamification-rule-row-${rule.key}`}>
                <td><div className="n">{RULE_COPY[rule.key].name}</div><div className="sub">{RULE_COPY[rule.key].sub}</div></td>
                <td className="r">
                  <Input
                    className="gm-num ml-auto"
                    inputMode="numeric"
                    fullWidth={false}
                    style={{ width: 84 }}
                    value={String(rule.points)}
                    disabled={disabled}
                    onChange={(e) => patch(rule.key, { points: toInt(e.target.value) ?? 0 })}
                    data-testid={`gamification-rule-points-${rule.key}`}
                  />
                </td>
                <td className="r">
                  {rule.key === 'lead_captured'
                    ? (
                      <Input
                        className="gm-num ml-auto"
                        inputMode="numeric"
                        fullWidth={false}
                        style={{ width: 84 }}
                        placeholder="—"
                        value={rule.dailyCap === undefined ? '' : String(rule.dailyCap)}
                        disabled={disabled}
                        onChange={(e) => { const v = toInt(e.target.value); patch(rule.key, { dailyCap: v === undefined || v === 0 ? undefined : v }); }}
                        data-testid={`gamification-rule-cap-${rule.key}`}
                      />
                    )
                    : <span className="sub">—</span>}
                </td>
                <td className="r">
                  {rule.key === 'lead_won'
                    ? (
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        <Input
                          className="gm-num"
                          style={{ width: 64 }}
                          inputMode="numeric"
                          fullWidth={false}
                          value={rule.reversalDays === undefined ? '' : String(rule.reversalDays)}
                          disabled={disabled}
                          placeholder="—"
                          // Campo vazio (ou 0) = SEM prazo: omite a chave no PUT
                          // e o backend estorna sempre. Antes mandava `0`, que o
                          // service lia como "nunca estorna" (`ageDays > 0` é
                          // sempre verdade) — o oposto do que a tela sugeria.
                          onChange={(e) => { const v = toInt(e.target.value); patch(rule.key, { reversalDays: v === undefined || v === 0 ? undefined : v }); }}
                          data-testid={`gamification-rule-reversal-${rule.key}`}
                        /> <span className="sub">dias</span>
                      </span>
                    )
                    : <span className="sub">—</span>}
                </td>
                <td className="r"><Switch size="sm" checked={rule.enabled} onChange={(v) => patch(rule.key, { enabled: v })} disabled={disabled} aria-label={`${RULE_COPY[rule.key].name} ativo`} data-testid={`gamification-rule-enabled-${rule.key}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && <div className="text-xs mt-2" style={{ color: 'var(--theme-error-fg)' }} data-testid="gamification-rules-error">{error}</div>}
      <div className="gm-hl">
        <div className="hic"><Icon name="info" size={16} /></div>
        <div><div className="ht">Por que captura vale pouco e venda vale muito</div><div className="hs">Assim não compensa inflar cadastros: seriam 10 clientes reais confirmando no WhatsApp para empatar com uma venda. Venda cancelada dentro do prazo de estorno devolve os pontos.</div></div>
      </div>
    </SettingsSection>
  );
};
PointsRulesTable.displayName = 'PointsRulesTable';
